import { PhotoRecord } from "@/lib/catalog"
import { useTranslation } from "react-i18next"
import { PhotoFlagStatus, PhotoFlagValues, PhotoTag, RatingValues } from "@/lib/catalog/types"
import { ColorTag, ColorTagValues, CullingValues, isColorTag, isCullingTag } from "@/utils/exif"
import { CheckboxItem } from "../ui/Checkbox"
import { Button } from "../ui/Button"
import { Dialog } from "../ui/Dialog"
import { atom, useAtom, useAtomValue } from "jotai"
import {
	allPhotosAtom,
	colorPhotosCountAtom,
	displayGroupPhotosCountAtom,
	flagPhotosCountAtom,
	starRatingPhotosCountAtom,
} from "@/store/photo/photos-state"
import { finishCulling } from "@/store/project/project"
import { Icon } from "../icons/Icon"
import { CandidateBadgeIcon, KeeperIcon, RejectBadgeIcon } from "../icons/CullingIcon"
import clsx from "clsx"
import { projectAtom, selectedTagsAtom } from "@/store/project/projects-state"
import { analytics } from "@/telemetry/amplitude"
import {
	cullingSettingsAtom,
	getCullingTag,
	isShowingRejectsAtom,
	isShowingRejectsOrKeepersAtom,
	isSuggestingKeepersAtom,
	keeperPhotosAtom,
	suggestedKeeperCountAtom,
} from "@/store/importing/culling-state"
import { store } from "@/store/store"
import { getProjectModeTimeSpentMinutes } from "@/store/project/project-session-timing"
import { getCullingSettingsEventProperties } from "@/store/importing/culling-event-utils"
import { colorTagKeys, cullingTagKeys, photoFlagKeys } from "@/i18n/dual-role-strings"

const photoSelectionResultAtom = atom<{
	unselected: PhotoRecord[]
	selected: PhotoRecord[]
	selectedKeepers: PhotoRecord[]
	selectedCandidates: PhotoRecord[]
	selectedRejects: PhotoRecord[]
}>((get) => {
	const allPhotos = get(allPhotosAtom)
	const selectedTags = get(selectedTagsAtom)

	const selected: PhotoRecord[] = []
	const unselected: PhotoRecord[] = []
	const selectedKeepers: PhotoRecord[] = []
	const selectedCandidates: PhotoRecord[] = []
	const selectedRejects: PhotoRecord[] = []
	allPhotos.map((photo) => {
		const isSelected = isPhotoSelected(photo, selectedTags)
		if (isSelected) {
			selected.push(photo)
			const cullingTag = getCullingTag(photo)
			if (cullingTag === "Keeper") {
				selectedKeepers.push(photo)
			} else if (cullingTag === "Candidate") {
				selectedCandidates.push(photo)
			} else if (cullingTag === "Reject") {
				selectedRejects.push(photo)
			}
		} else {
			unselected.push(photo)
		}
	})
	return { unselected, selected, selectedKeepers, selectedCandidates, selectedRejects }
})

export const AllPhotoSelectionKeys = [
	...RatingValues,
	...PhotoFlagValues,
	...ColorTagValues,
	...CullingValues,
]

const isPhotoSelected = (photo: PhotoRecord, selectedTags: Set<PhotoTag>) => {
	const cullingTag = getCullingTag(photo)
	const result =
		selectedTags.has(photo.colorTag ?? "No Color") || // i18n-ignore: ColorTag enum value
		selectedTags.has(photo.rating ?? 0) ||
		selectedTags.has(photo.flag ?? "unflagged") ||
		(cullingTag && selectedTags.has(cullingTag))
	return result
}

export function FinishCullingDialog() {
	const { t } = useTranslation("components")
	const [selectedTags, setSelectedTags] = useAtom(selectedTagsAtom)
	const project = useAtomValue(projectAtom)

	const allPhotos = useAtomValue(allPhotosAtom)
	const { unselected, selected, selectedKeepers, selectedCandidates, selectedRejects } =
		useAtomValue(photoSelectionResultAtom)

	const colorCount = useAtomValue(colorPhotosCountAtom)
	const starCount = useAtomValue(starRatingPhotosCountAtom)
	const flagCount = useAtomValue(flagPhotosCountAtom)
	const displayGroupCount = useAtomValue(displayGroupPhotosCountAtom)
	const isShowingKeepersOrRejects = useAtomValue(isShowingRejectsOrKeepersAtom)
	const isSuggestingKeepers = useAtomValue(isSuggestingKeepersAtom)
	const isShowingRejects = useAtomValue(isShowingRejectsAtom)

	const toggleItem = (key: PhotoTag, isChecked: boolean) => {
		if (isChecked) {
			selectedTags.add(key)
			setSelectedTags(new Set(selectedTags))
		} else {
			selectedTags.delete(key)
			setSelectedTags(new Set(selectedTags))
		}
	}

	const checkAll = () => {
		setSelectedTags(new Set(AllPhotoSelectionKeys))
	}

	const checkNone = () => {
		setSelectedTags(new Set())
	}

	const displayName = (key: PhotoTag) => {
		if (typeof key === "number") {
			return t("finishCullingDialog.starDisplayName", { count: key, total: starCount[key] })
		}

		if (isColorTag(key)) {
			return `${t(colorTagKeys[key])} (${colorCount[key]})`
		}

		if (isCullingTag(key)) {
			const countMap = {
				Keeper: displayGroupCount.keepers,
				Candidate: displayGroupCount.candidates,
				Reject: displayGroupCount.rejects,
			}
			return `${t(cullingTagKeys[key])} (${countMap[key]})`
		}

		return `${t(photoFlagKeys[key as PhotoFlagStatus])} (${flagCount[key]})`
	}

	const getColor = (key: ColorTag) => {
		switch (key) {
			case "Red":
				return "bg-red-500"
			case "Yellow":
				return "bg-yellow-500"
			case "Green":
				return "bg-green-500"
			case "Blue":
				return "bg-blue-500"
			case "Purple":
				return "bg-purple-500"
		}
	}
	const displayIcon = (key: PhotoTag) => {
		if (typeof key === "number") {
			return <Icon name={`star${key}`} size={18} />
		} else if (isColorTag(key)) {
			if (key === "No Color") {
				return <Icon className="-m-px" name="no_color" size={18} />
			} else {
				return (
					<div className={clsx(getColor(key as ColorTag), "size-4 rounded-full")}></div>
				)
			}
		} else {
			switch (key) {
				case "flagged":
					return <Icon name="flagged" size={16} />
				case "unflagged":
					return <Icon name="unflagged" size={16} />
				case "rejected":
					return <Icon name="rejected" size={16} />
				case "Keeper":
					return <KeeperIcon size={16} />
				case "Candidate":
					return <CandidateBadgeIcon size={16} />
				case "Reject":
					return <RejectBadgeIcon size={16} />
			}
		}
	}
	const renderItem = (key: PhotoTag) => {
		return (
			<CheckboxItem
				trackingLabel={`select_photos_${key}`}
				id={String(key)}
				checkedState={selectedTags.has(key)}
				content={
					<div className="flex flex-row items-center space-x-1">
						{displayIcon(key)}
						<div>{displayName(key)}</div>
					</div>
				}
				onCheckChanged={(key, isChecked) => {
					const rating = Number(key)
					if (!isNaN(rating)) {
						toggleItem(rating as PhotoTag, isChecked)
					} else {
						toggleItem(key as PhotoTag, isChecked)
					}
				}}
			/>
		)
	}

	return (
		<Dialog
			contentSize={isShowingKeepersOrRejects ? "md-h-auto" : "sm-h-auto"}
			trackingViewName="select_photos_to_edit_dialog"
		>
			<div className="flex w-full flex-col items-start justify-start space-y-6">
				<div className="flex flex-col space-y-2">
					<div className="text-start text-md font-bold">
						{t("finishCullingDialog.title")}
					</div>
					<div className="text-start text-sm text-base-300">
						{t("finishCullingDialog.selectedPhotosDescription", {
							count: selected.length,
						})}
					</div>
				</div>
				<div className="flex w-full flex-col items-start justify-start space-y-6">
					<div
						className={clsx(
							"grid w-full",
							isShowingKeepersOrRejects ? "grid-cols-4" : "grid-cols-3",
						)}
					>
						<div className="flex h-full flex-row space-x-2">
							<div className="flex flex-col items-start justify-start space-y-3">
								<div className="text-sm text-base-400">
									{t("finishCullingDialog.flag")}
								</div>
								{renderItem("flagged")}
								{renderItem("unflagged")}
								{renderItem("rejected")}
							</div>
						</div>
						<div className="flex h-full flex-row space-x-2">
							<div className="flex h-[200px] w-[0.5px] shrink-0 bg-base-700"></div>
							<div className="flex flex-col items-start justify-start space-y-3">
								<div className="text-sm text-base-400">
									{t("finishCullingDialog.starRating")}
								</div>
								{renderItem(5)}
								{renderItem(4)}
								{renderItem(3)}
								{renderItem(2)}
								{renderItem(1)}
								{renderItem(0)}
							</div>
						</div>
						<div className="flex h-full flex-row space-x-2">
							<div className="flex h-[200px] w-[0.5px] shrink-0 bg-base-700"></div>
							<div className="flex flex-col items-start justify-start space-y-3">
								<div className="text-sm text-base-400">
									{t("finishCullingDialog.colorTag")}
								</div>
								{renderItem("Red")}
								{renderItem("Yellow")}
								{renderItem("Green")}
								{renderItem("Blue")}
								{renderItem("Purple")}
								{renderItem("No Color")} {/* i18n-ignore: ColorTag enum value */}
							</div>
						</div>
						{isShowingKeepersOrRejects && (
							<div className="flex h-full flex-row space-x-2">
								<div className="flex h-[200px] w-[0.5px] shrink-0 bg-base-700"></div>
								<div className="flex flex-col items-start justify-start space-y-3">
									<div className="text-sm text-base-400">
										{t("finishCullingDialog.smartCulling")}
									</div>
									{isSuggestingKeepers && renderItem("Keeper")}
									{renderItem("Candidate")}
									{isShowingRejects && renderItem("Reject")}
								</div>
							</div>
						)}
					</div>
					<div className="flex w-full flex-row justify-between">
						<div className="flex flex-row space-x-2">
							<Button
								trackingLabel="check_all"
								aria-label={t("finishCullingDialog.ariaCheckAll")}
								size="sm"
								onClick={checkAll}
							>
								{t("finishCullingDialog.checkAll")}
							</Button>
							<Button
								trackingLabel="check_none"
								aria-label={t("finishCullingDialog.ariaCheckNone")}
								size="sm"
								onClick={checkNone}
							>
								{t("finishCullingDialog.checkNone")}
							</Button>
						</div>
						<div className="flex flex-row space-x-2">
							<Button
								trackingLabel="confirm"
								aria-label={t("finishCullingDialog.ariaConfirmEditSelection")}
								variant="primary"
								size="sm"
								onClick={() => {
									void finishCulling(unselected)

									const totalPhotos = allPhotos.length
									const cullingSettingsProperties =
										getCullingSettingsEventProperties({
											cullingSettings: store.get(cullingSettingsAtom),
											suggestKeepersCount:
												store.get(suggestedKeeperCountAtom),
											totalCount: totalPhotos,
											includeAutoAdvance: true,
										})
									const { cullingTimeSpentMinutes } =
										getProjectModeTimeSpentMinutes(project?.id)
									const cullingDurationMinutes = cullingTimeSpentMinutes
									const cullingDurationPer1kPhotosMinutes =
										totalPhotos > 0
											? Math.floor(
													(cullingDurationMinutes * 1000) / totalPhotos,
												)
											: 0

									const info = {
										...(project?.id ? { projectId: project.id } : {}),
										...cullingSettingsProperties,
										selectedPhotos: selected.length,
										keeperPhotos: store.get(keeperPhotosAtom).length,
										candidatePhotos: displayGroupCount.candidates,
										rejectPhotos: displayGroupCount.rejects,
										selectedKeeperPhotos: selectedKeepers.length,
										selectedCandidatePhotos: selectedCandidates.length,
										selectedRejectPhotos: selectedRejects.length,
										cullingDurationMinutes,
										cullingDurationPer1kPhotosMinutes,
									}
									analytics.trackEvent({
										label: "finish_culling",
										type: "event",
										customProperties: info,
									})
								}}
								disabled={selectedTags.size === 0 || selected.length === 0}
							>
								{t("finishCullingDialog.editSelected", { count: selected.length })}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</Dialog>
	)
}
